import { Box, Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	Fragment,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { YESNO, YESNOLIST } from '@/common/constants/common';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import IconButton from '@mui/material/IconButton';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditShareCaseServiceForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {};

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			otherOrganisationList: Yup.array().when('otherOrganisationServing', {
				is: (val: any) => val === '1',
				then: (schema) =>
					schema.of(
						Yup.object().shape({
							clientName: Yup.string().required(t('errorMessage.pleaseEnter')),
							organization: Yup.string().required(
								t('errorMessage.pleaseEnter')
							),
							name: Yup.string().required(t('errorMessage.pleaseEnter')),
							telNo: Yup.string()
								.required(t('errorMessage.pleaseEnter'))
								.test(
									eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))
								),
						})
					),
				otherwise: (schema) => schema,
			}),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchOtherOrganisationServing = methods.watch(
			'otherOrganisationServing'
		);

		const {
			fields: shareCaseFields,
			append: shareCaseAppend,
			replace: shareCaseReplace,
			update: shareCaseUpdate,
		}: any = useFieldArray({
			control: methods.control,
			name: 'otherOrganisationList',
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		const addShareCase = () => {
			shareCaseAppend({});
		};

		// Removing a row shifts the field name of every row below it. The inputs are
		// controllers bound to those names, so with remove() they keep the value they
		// held under the old index and the last row's values drop out of the form
		// state - the row still shows them, but submitting reports it as empty.
		// Rebuilding the list with replace() hands every row a fresh key, so they
		// remount against the shifted values.
		const removeShareCase = (index: number) => {
			shareCaseReplace(
				(methods.getValues('otherOrganisationList') || []).filter(
					(_: any, i: number) => i !== index
				)
			);
		};

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.shareCaseService')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='otherOrganisationServing'
									label={t('preAdmission.otherOrganisationServingSameCase')}
									options={YESNOLIST}
									handleChange={() => {
										shareCaseReplace([]);
									}}
								/>
							</Grid>
						</Grid>
						{watchOtherOrganisationServing
							? shareCaseFields.map((item: any, index: number) => (
									<Fragment key={item.id}>
										<Box
											sx={{
												display: 'flex',
												justifyContent: 'space-between',
											}}
											marginBottom={1}
										>
											<Typography variant='body1' marginBottom={2}>
												{t('preAdmission.organisation') + ` ${index + 1}`}
											</Typography>
											<IconButton
												onClick={() => {
													removeShareCase(index);
												}}
											>
												<CloseOutlinedIcon />
											</IconButton>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={2}>
											<Grid size={6}>
												<AppTextField
													control={methods.control}
													name={`otherOrganisationList[${index}].clientName`}
													label={t('preAdmission.clientName') + '*'}
												/>
											</Grid>
											<Grid size={6}>
												<AppTextField
													control={methods.control}
													name={`otherOrganisationList[${index}].organization`}
													label={t('preAdmission.organisation') + '*'}
												/>
											</Grid>
											<Grid size={6}>
												<AppTextField
													control={methods.control}
													name={`otherOrganisationList[${index}].name`}
													label={t('preAdmission.name') + '*'}
												/>
											</Grid>
											<Grid size={6}>
												<AppTextField
													control={methods.control}
													name={`otherOrganisationList[${index}].telNo`}
													label={t('preAdmission.telNo') + '*'}
												/>
											</Grid>
										</Grid>
									</Fragment>
								))
							: ''}
						{watchOtherOrganisationServing
							? shareCaseFields.length < 5 && (
									<Grid container spacing={3} marginTop={0} marginBottom={3}>
										<Grid size={12}>
											<Button
												startIcon={<AddCircleOutlineOutlinedIcon />}
												size='large'
												onClick={addShareCase}
											>
												{t('button.newOrganisation')}
											</Button>
										</Grid>
									</Grid>
								)
							: ''}
					</form>
				</FormProvider>
			</>
		);
	}
);

EditShareCaseServiceForm.displayName = 'EditShareCaseServiceForm';

export default EditShareCaseServiceForm;
